/**
 * Quick tender amounts for a cash sale: the exact amount, then the round
 * figures a customer is likely to hand over (the next 50, 100, 200, 500 and
 * 1000). At most six, ascending, each at least the amount due, no repeats.
 *
 * Works in centavos so a total like 385.50 never picks up a float tail. A due
 * with a fraction of a centavo (a percent discount or fee can leave one, the
 * order total keeps four places) rounds UP: "Exact" has to cover the total, and
 * a figure rounded down would leave the charge a hair short.
 */
const STEPS = [50, 100, 200, 500, 1000] as const;

export function quickAmounts(total: number): number[] {
  if (!Number.isFinite(total) || total <= 0) return [];
  // The epsilon absorbs binary float noise (385.5 × 100 is 38550.000000000004)
  // without swallowing a real quarter-centavo.
  const centavos = Math.ceil(total * 100 - 1e-6);
  const out = new Set<number>([centavos]);
  for (const step of STEPS) {
    const stepCentavos = step * 100;
    out.add(Math.ceil(centavos / stepCentavos) * stepCentavos);
  }
  return [...out]
    .sort((a, b) => a - b)
    .slice(0, 6)
    .map((c) => c / 100);
}

/** "385.50" for a figure with centavos, "400" for a whole one. */
export function quickAmountLabel(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}
